import { useEffect, useRef } from "react";
import { useDispatch, useStore } from "react-redux";
import { useHistory } from "react-router-dom";

import { hubBaseUrl, refreshHubToken, redirectToHubSignin } from "../../../utils/hub-sso";
import { setUserTokenAction, resetUserTokenAction } from "../../../reducers/app.reducer";

/**
 * The Semi360 Hub's own top bar, loaded from the Hub rather than reimplemented
 * — the suite's one header (product switcher, org chip, account menu, Sami),
 * in Shadow DOM so the Vuexy stylesheet cannot reach it. Ported from
 * chat360's HubHeader.tsx.
 *
 * The mount is idempotent (script keyed by id, element created only into an
 * empty container): React 18 StrictMode double-invokes effects in dev, and
 * Sample360 once stacked two headers exactly that way.
 *
 * This component also owns keeping the Hub JWT fresh: the token lives 300
 * seconds, so it is re-minted on an interval while any authed screen is
 * mounted, dispatched into the same redux slot every service file reads.
 */

const SCRIPT_ID = "semi360-header-embed";
const STYLE_ID = "semi360-hub-chrome-css";
const HEADER_H = 46; // the embed's own bar height
const REFRESH_MS = 4 * 60 * 1000; // re-mint before the 300s JWT expires

// Vuexy's side menu is fixed at top:0 with a z-index in the thousands, which
// buries the suite header's left edge. Under Hub chrome the menu starts below
// the header instead — the same full-width-header layout every other product
// shows.
const CHROME_CSS = `
.hub-chrome .main-menu{top:${HEADER_H}px;height:calc(100% - ${HEADER_H}px)}
.hub-chrome semi360-header{z-index:1040!important}
`;

export default function HubHeaderComponent() {
  const hostRef = useRef(null);
  const dispatch = useDispatch();
  const store = useStore();
  const history = useHistory();

  useEffect(() => {
    if (!document.getElementById(STYLE_ID)) {
      const css = document.createElement("style");
      css.id = STYLE_ID;
      css.textContent = CHROME_CSS;
      document.head.appendChild(css);
    }
    if (!document.getElementById(SCRIPT_ID)) {
      const s = document.createElement("script");
      s.id = SCRIPT_ID;
      s.src = hubBaseUrl() + "/embed/header.js";
      s.async = true;
      document.head.appendChild(s);
    }
    const host = hostRef.current;
    if (host && !host.firstChild) {
      const el = document.createElement("semi360-header");
      el.setAttribute("product", "ecom360");
      el.setAttribute("base", hubBaseUrl());
      host.appendChild(el);
    }
  }, []);

  useEffect(() => {
    const tick = async () => {
      const token = await refreshHubToken();
      if (token) {
        // setUserTokenAction sets token AND user from its payload — pass the
        // current user through or the refresh would blank the header avatar.
        const user = store.getState().app.user;
        dispatch(setUserTokenAction({ token, user }));
      } else {
        // The Hub session ended (signed out elsewhere, or revoked). Drop the
        // local session too and go back through the Hub's door.
        dispatch(resetUserTokenAction(null));
        redirectToHubSignin();
      }
    };
    const t = setInterval(tick, REFRESH_MS);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // history is unused today but kept in signature parity with the header's
  // future navigation events (semi360:switch-product is cancelable).
  void history;

  return <div ref={hostRef} />;
}
